import { ColumnDetails, ColumnType, ColumnValue } from '../../../../grid/types/common';
import { CompanyTypeData } from '../../../../company/types/company-type-data';
import { PaymentPlanType } from '../../../../company/types/PaymentPlanType';
import { CompanyStatus } from '../../../../company/types/company-status';
import { ExpiryDateFilter } from '../../types/companies-grid-filters';
import { getTranslatedPaymentPlan } from '../../../../company/common/utils/PaymentPlan';
import { LicenseProductStatus } from '../../../../company/common/licensingGrid/types/licensing-grid-license';
import { getTranslatedCompanyStatus } from '../../../../company/common/utils/CompanyStatus';
import { MspInProductTrialStatus } from '../../../../company/types/msp-in-product-trial-status';

export enum ColumnIndexes {
  COMPANY_NAME,
  COMPANY_TYPE,
  COMPANY_STATUS,
  CAN_BE_MANAGED_BY_ABOVE,
  PAYMENT_PLAN,
  PRODUCT_NAME,
  PRODUCT_TYPE,
  PRODUCT_STATUS,
  LICENSE_KEY,
  EXPIRY_DATE,
  TOTAL_SEATS,
  USAGE_BREAKDOWN,
  COMPANY_ID,
  AUTO_RENEWAL,
  MINIMUM_USAGE,
  SUBSCRIPTION_END_DATE,
  UNLICENSED,
  MSP_IN_PRODUCT_TRIAL_STATUS,
}

export enum ColumnKeys {
  COMPANY_NAME = 'companyName',
  COMPANY_TYPE = 'companyType',
  COMPANY_STATUS = 'companyStatus',
  CAN_BE_MANAGED_BY_ABOVE = 'canBeManagedByAbove',
  PAYMENT_PLAN = 'paymentPlan',
  PRODUCT_NAME = 'productName',
  PRODUCT_TYPE = 'productType',
  PRODUCT_STATUS = 'productStatus',
  LICENSE_KEY = 'licenseKey',
  EXPIRY_DATE = 'expiryDate',
  TOTAL_SEATS = 'totalSeats',
  USAGE_BREAKDOWN = 'usageBreakdown',
  COMPANY_ID = 'companyId',
  AUTO_RENEWAL = 'autoRenewal',
  MINIMUM_USAGE = 'minimumUsage',
  SUBSCRIPTION_END_DATE = 'subscriptionEndDate',
  UNLICENSED = 'unlicensed',
  MSP_IN_PRODUCT_TRIAL_STATUS = 'mspInProductTrialStatus',
}

export interface UpdateFiltersVisibilityRequest {
  showFilters: boolean;
}

export const DEFAULT_VISIBLE_COLUMNS = [
  ColumnIndexes.COMPANY_NAME,
  ColumnIndexes.COMPANY_TYPE,
  ColumnIndexes.COMPANY_STATUS,
  ColumnIndexes.CAN_BE_MANAGED_BY_ABOVE,
  ColumnIndexes.PAYMENT_PLAN,
  ColumnIndexes.PRODUCT_NAME,
  ColumnIndexes.LICENSE_KEY,
  ColumnIndexes.EXPIRY_DATE,
  ColumnIndexes.TOTAL_SEATS,
  ColumnIndexes.USAGE_BREAKDOWN,
];

export const PREDEFINED_FILTERS_KEYS = [ColumnKeys.COMPANY_NAME, ColumnKeys.COMPANY_TYPE];

export const DEFAULT_VALUE_FOR_FILTERS_VISIBILITY = true;

export const FILTERS = [
  { [ColumnKeys.COMPANY_NAME]: { contains: '' } },
  { [ColumnKeys.COMPANY_TYPE]: { in: [] } },
  { [ColumnKeys.CAN_BE_MANAGED_BY_ABOVE]: { in: [] } },
  { [ColumnKeys.PAYMENT_PLAN]: { in: [] } },
  { [ColumnKeys.COMPANY_STATUS]: { in: [] } },
  { [ColumnKeys.PRODUCT_STATUS]: { in: [] } },
];

export const DEFAULT_COLUMN_CONFIGS = [
  {
    key: ColumnKeys.COMPANY_NAME,
    width: '180px',
  },
  {
    key: ColumnKeys.COMPANY_TYPE,
    width: '130px',
  },
  {
    key: ColumnKeys.COMPANY_STATUS,
    width: '130px',
  },
  {
    key: ColumnKeys.CAN_BE_MANAGED_BY_ABOVE,
    width: '90px',
  },
  {
    key: ColumnKeys.PAYMENT_PLAN,
    width: '110px',
  },
  {
    key: ColumnKeys.PRODUCT_NAME,
    width: '240px',
  },
  {
    key: ColumnKeys.PRODUCT_TYPE,
    width: '120px',
  },
  {
    key: ColumnKeys.PRODUCT_STATUS,
    width: '130px',
  },
  {
    key: ColumnKeys.LICENSE_KEY,
    width: '130px',
  },
  {
    key: ColumnKeys.EXPIRY_DATE,
    width: '110px',
  },
  {
    key: ColumnKeys.TOTAL_SEATS,
    width: '140px',
  },
  {
    key: ColumnKeys.USAGE_BREAKDOWN,
    width: '220px',
  },
  {
    key: ColumnKeys.UNLICENSED,
    width: '110px',
  },
  {
    key: ColumnKeys.COMPANY_ID,
    width: '200px',
  },
  {
    key: ColumnKeys.AUTO_RENEWAL,
    width: '110px',
  },
  {
    key: ColumnKeys.MINIMUM_USAGE,
    width: '130px',
  },
  {
    key: ColumnKeys.SUBSCRIPTION_END_DATE,
    width: '160px',
  },
  {
    key: ColumnKeys.MSP_IN_PRODUCT_TRIAL_STATUS,
    width: '120px',
  },
];

export function getColumnDetails(
  translateFn,
  companyTypes: CompanyTypeData[]
): ColumnDetails<ColumnIndexes, ColumnKeys>[] {
  return [
    {
      column: ColumnIndexes.COMPANY_NAME,
      key: ColumnKeys.COMPANY_NAME,
      columnName: translateFn('Company name'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
      filteringDisabled: false,
      flexGrow: 2,
    },
    {
      column: ColumnIndexes.COMPANY_TYPE,
      key: ColumnKeys.COMPANY_TYPE,
      columnName: translateFn('Company type'),
      type: ColumnType.ENUM,
      values: getCompanyTypeColumnValues(companyTypes),
      removable: false,
      sortable: false,
      filteringDisabled: false,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.COMPANY_STATUS,
      key: ColumnKeys.COMPANY_STATUS,
      columnName: translateFn('Company status'),
      type: ColumnType.ENUM,
      values: getCompanyStatusColumnValues(translateFn),
      removable: true,
      sortable: false,
      filteringDisabled: false,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.CAN_BE_MANAGED_BY_ABOVE,
      key: ColumnKeys.CAN_BE_MANAGED_BY_ABOVE,
      columnName: translateFn('Managed'),
      type: ColumnType.ENUM,
      values: getCanBeManagedByAboveColumnValues(translateFn),
      removable: true,
      sortable: false,
      filteringDisabled: false,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.PAYMENT_PLAN,
      key: ColumnKeys.PAYMENT_PLAN,
      columnName: translateFn('Payment plan'),
      type: ColumnType.ENUM,
      values: getPaymentPlanColumnValues(),
      removable: true,
      sortable: false,
      filteringDisabled: false,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.PRODUCT_NAME,
      key: ColumnKeys.PRODUCT_NAME,
      columnName: translateFn('Product name'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: true,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.PRODUCT_TYPE,
      key: ColumnKeys.PRODUCT_TYPE,
      columnName: translateFn('Product type'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: true,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.PRODUCT_STATUS,
      key: ColumnKeys.PRODUCT_STATUS,
      columnName: translateFn('Product status'),
      type: ColumnType.ENUM,
      values: getProductStatusColumnValues(translateFn),
      removable: true,
      sortable: false,
      filteringDisabled: false,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.LICENSE_KEY,
      key: ColumnKeys.LICENSE_KEY,
      columnName: translateFn('License key'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: false,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.EXPIRY_DATE,
      key: ColumnKeys.EXPIRY_DATE,
      columnName: translateFn('Expiry date'),
      type: ColumnType.ENUM,
      values: getExpiryDateColumnValues(translateFn),
      removable: true,
      sortable: false,
      filteringDisabled: false,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.TOTAL_SEATS,
      key: ColumnKeys.TOTAL_SEATS,
      columnName: translateFn('Total seats'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: true,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.USAGE_BREAKDOWN,
      key: ColumnKeys.USAGE_BREAKDOWN,
      columnName: translateFn('Usage breakdown'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: true,
      flexGrow: 2,
    },
    {
      column: ColumnIndexes.UNLICENSED,
      key: ColumnKeys.UNLICENSED,
      columnName: translateFn('Unlicensed'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: true,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.COMPANY_ID,
      key: ColumnKeys.COMPANY_ID,
      columnName: translateFn('Company ID'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: false,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.AUTO_RENEWAL,
      key: ColumnKeys.AUTO_RENEWAL,
      columnName: translateFn('Auto-renewal'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: true,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.MINIMUM_USAGE,
      key: ColumnKeys.MINIMUM_USAGE,
      columnName: translateFn('Minimum usage'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: true,
      flexGrow: 1,
    },
    {
      column: ColumnIndexes.SUBSCRIPTION_END_DATE,
      key: ColumnKeys.SUBSCRIPTION_END_DATE,
      columnName: translateFn('Subscription end date'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      filteringDisabled: true,
    },
    {
      column: ColumnIndexes.MSP_IN_PRODUCT_TRIAL_STATUS,
      key: ColumnKeys.MSP_IN_PRODUCT_TRIAL_STATUS,
      columnName: translateFn('MSP trials status'),
      type: ColumnType.ENUM,
      values: getMspInProductTrialStatusValues(translateFn),
      removable: true,
      sortable: false,
      filteringDisabled: false,
    },
  ];
}

function getCompanyTypeColumnValues(companyTypes: CompanyTypeData[]): ColumnValue[] {
  return companyTypes.map(companyType => {
    return { label: companyType.name, value: companyType.value };
  });
}

function getCompanyStatusColumnValues(translateFn): ColumnValue[] {
  return [
    { label: getTranslatedCompanyStatus(true), value: CompanyStatus.Suspended },
    { label: getTranslatedCompanyStatus(false), value: CompanyStatus.Active },
  ];
}

function getCanBeManagedByAboveColumnValues(translateFn): ColumnValue[] {
  return [
    { label: translateFn('No'), value: 0 },
    { label: translateFn('Yes'), value: 1 },
  ];
}

function getPaymentPlanColumnValues(): ColumnValue[] {
  return [
    { label: getTranslatedPaymentPlan(PaymentPlanType.LICENSE_NONE), value: PaymentPlanType.LICENSE_NONE },
    { label: getTranslatedPaymentPlan(PaymentPlanType.YEARLY), value: PaymentPlanType.YEARLY },
    { label: getTranslatedPaymentPlan(PaymentPlanType.MONTHLY), value: PaymentPlanType.MONTHLY },
  ];
}

function getExpiryDateColumnValues(translateFn): ColumnValue[] {
  return [
    { label: translateFn('In less than 30 days'), value: ExpiryDateFilter.IN_30_DAYS },
    { label: translateFn('In less than 90 days'), value: ExpiryDateFilter.IN_90_DAYS },
  ];
}

function getProductStatusColumnValues(translateFn): ColumnValue[] {
  return [
    { label: translateFn('Active'), value: LicenseProductStatus.ACTIVE },
    { label: translateFn('Expired'), value: LicenseProductStatus.EXPIRED },
    { label: translateFn('Trial'), value: LicenseProductStatus.TRIAL },
    { label: translateFn('No products'), value: LicenseProductStatus.NO_PRODUCTS },
  ];
}

function getMspInProductTrialStatusValues(translateFn): ColumnValue[] {
  return [
    { label: translateFn('Not eligible'), value: MspInProductTrialStatus.NOT_APPLICABLE },
    { label: translateFn('Available for trial'), value: MspInProductTrialStatus.TRIAL_AVAILABLE },
    { label: translateFn('Trial ongoing'), value: MspInProductTrialStatus.IN_TRIAL },
    { label: translateFn('Trial expired'), value: MspInProductTrialStatus.TRIAL_EXPIRED },
  ];
}
